import { motion } from "framer-motion";
import { BedDouble, Bath, Maximize, MapPin, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { EASE } from "@/components/Reveal";

export const PropertyCard = ({ listing, index = 0 }) => {
  const { t } = useLanguage();
  const statusLabel = {
    sale: t.listingsPage.forSale,
    sold: t.listingsPage.sold,
    lease: t.listingsPage.forLease,
  }[listing.status];

  const price = listing.status === "lease"
    ? `$${listing.price.toLocaleString()}${t.listingsPage.perMonth}`
    : `$${listing.price.toLocaleString()}`;

  return (
    <motion.article
      data-testid={`property-card-${listing.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: EASE }}
      whileHover={{ y: -6 }}
      className="group bg-white border border-sand/80 overflow-hidden shadow-[0_2px_24px_rgba(13,19,26,0.05)] hover:shadow-[0_16px_48px_rgba(13,19,26,0.12)] transition-shadow duration-500"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={listing.image}
          alt={`${listing.address}, ${listing.neighborhood}`}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-60" />
        <span
          data-testid={`property-status-${listing.id}`}
          className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full ${
            listing.status === "sold" ? "bg-charcoal text-cream" : listing.status === "lease" ? "bg-emerald text-cream" : "bg-gold text-ink"
          }`}
        >
          {statusLabel}
        </span>
        <span className="absolute bottom-4 left-4 font-serif text-2xl text-cream drop-shadow-md" data-testid={`property-price-${listing.id}`}>
          {price}
        </span>
      </div>
      <div className="p-5">
        <p className="font-serif text-lg text-ink leading-snug">{listing.address}</p>
        <p className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#8C96A4]">
          <MapPin className="w-3 h-3 text-gold" /> {listing.neighborhood}
        </p>
        <div className="mt-4 pt-4 border-t border-sand/70 flex items-center gap-5 text-[13px] text-[#525D6B]">
          <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-gold" /> {listing.beds} {t.listingsSection.beds}</span>
          <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-gold" /> {listing.baths} {t.listingsSection.baths}</span>
          <span className="flex items-center gap-1.5"><Maximize className="w-4 h-4 text-gold" /> {listing.sqft.toLocaleString()} {t.listingsSection.sqft}</span>
        </div>
        {listing.zillow_url && (
          <a
            data-testid={`property-zillow-${listing.id}`}
            href={listing.zillow_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark hover:text-ink transition-colors"
          >
            {t.listingsSection.viewZillow} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </motion.article>
  );
};
